import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Drawer, Dropdown, Menu, Modal, Pagination, Popconfirm, Table, Tooltip } from 'antd';
import { capitalize, get, indexOf, isEmpty, isNull, upperCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getIsWriteAllAllowed,
  getIsWriteBrandedFixturesAllowed,
  getWriteMenuPermissions,
  getWriteTabsPermissions,
} from '../../store/user/user-reducer';
import { getAuditForGrid } from '../audit/audit-reducer';
import { getDoctorsForGrid } from '../doctors/doctors-selectors';
import { toggleSelectedLocations } from '../offices/offices-actions';
import {
  getFinanceForGrid,
  getItServicesForGrid,
  getLocationsForGrid,
  getMarketingForGrid,
  getMvcForGrid,
  getProfileForGrid,
  getProServicesForGrid,
  getPXPForGrid,
  getHRForGrid,
  getScheduleForGrid,
  getSelectedLocations,
  getSpecialtyForGrid,
  getTelecomForGrid,
  getVehForGrid,
  getMerchandisingForGrid,
  getVendorForGrid,
} from '../offices/offices-selectors';

import { EditActionCell } from '@med-fe/ui';
import DoctorPanel from '../../components/doctor-panel/DoctorPanel';
import CollapsiblePanel from '../collapsible-panel/CollapsiblePanel';
import AreaRegionDistrictBulkEdit from '../details/locations/AreaRegionDistrictBulkEdit';
import AreaRegionDistrictDetails from '../details/locations/AreaRegionDistrictDetails';
import LocationDetails from '../details/locations/LocationDetails';
import LinkCell from './cell-components/LinkCell';

import { grid_columns_map } from './grid-configs';
import { getScheduleColumns } from './grid-configs/schedule';

import { DeleteOutlined, FileExcelFilled } from '@ant-design/icons';
import { SelectAllButton, TextCell } from '@med-fe/ui';
import useVendorColumns from '../../common/hooks/use-custom-vendor-columns-hook';
import VendorDetails from '../details/vendor/VendorDetails';
import {
  deleteFile,
  downloadFile,
  downloadSnapshotFile,
  downloadValidateFile,
  processImport,
  validateFile,
} from '../import/import-actions';
import { getImports } from '../import/import-reducer';
import menuStyles from '../navbar/navbarMenu.module.scss';
import EditVendorDetails from '../vendors/EditVendorDetails';
import { getVendorMaintenanceForGrid } from '../vendors/vendors-selectors';
import { getBrandedFixturesForGrid } from '../branded-fixtures/branded-fixtures-selectors';
import EditBrandedFixtureDetails from '../branded-fixtures/EditBrandedFixtureDetails';
import styles from './BaseGrid.module.scss';

function BaseGrid({
  id,
  activeTab,
  total,
  search,
  setSearch,
  loading,
  isAllSelected,
  toggleIsAllSelected,
  scheduleSubtab = 'current',
  scheduleDate,
}: any) {
  const selectorMap: any = {
    locations: getLocationsForGrid,
    profile: getProfileForGrid,
    finance: getFinanceForGrid,
    marketing: getMarketingForGrid,
    schedule: getScheduleForGrid,
    mvc: getMvcForGrid,
    audit: getAuditForGrid,
    pro: getProServicesForGrid,
    it: getItServicesForGrid,
    veh: getVehForGrid,
    merchandising: getMerchandisingForGrid,
    telecom: getTelecomForGrid,
    doctors: getDoctorsForGrid,
    specialty: getSpecialtyForGrid,
    vendor: getVendorForGrid,
    vendorMaintenance: getVendorMaintenanceForGrid,
    brandedFixtures: getBrandedFixturesForGrid,
    pxp: getPXPForGrid,
    hr: getHRForGrid,
    import: getImports,
  };
  const data = useSelector((state) =>
    id === 'schedule' && scheduleSubtab === 'core' ? getScheduleForGrid(state, false) : selectorMap[id](state)
  );
  const dispatch = useDispatch();

  const isWriteAllAllowed = useSelector(getIsWriteAllAllowed);
  const isWriteBrandedFixturesAllowed = useSelector(getIsWriteBrandedFixturesAllowed);
  const isAddAllowed = id === 'brandedFixtures' ? isWriteBrandedFixturesAllowed : isWriteAllAllowed;
  const isBulkEditAllowed = isWriteAllAllowed && ['locations', 'marketing'].some((tab) => tab === activeTab);
  const locations = useSelector(getLocationsForGrid);
  const selectedLocationIDs = useSelector(getSelectedLocations);

  const { page, size } = search;
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});
  const [startSelection, setStartSelection] = useState(null);
  const [endSelection, setEndSelection] = useState(null);
  const tabsWithBulk = ['locations', 'marketing'];
  const [vendorDrawer, setVendorDrawer] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [rowDetail, setRowDetail] = useState<any>();
  const [brandedFixtureDrawer, setBrandedFixtureDrawer] = useState(false);
  const [selectedBrandedFixture, setSelectedBrandedFixture] = useState<any>(null);
  const vendorColumns = useVendorColumns();
  const gridColumns = { ...grid_columns_map, vendor: vendorColumns };

  const isImportEditAllowed = useSelector(getWriteMenuPermissions).import;
  const writeTabsPermissions = useSelector(getWriteTabsPermissions);
  const writeMenusPermissions = useSelector(getWriteMenuPermissions);
  const columns = useMemo(
    () =>
      (id === 'schedule' ? getScheduleColumns(scheduleSubtab, scheduleDate) : gridColumns[id]).map((col: any) => {
        const columnConfig = {
          ...col,
          title: (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {col.key === 'doNotPromote' ? (
                <>
                  Do Not Promote
                  <Tooltip title='By enabling this flag, this office will not be shown as a ‘Featured Office’ in PXP, but will still show as available for booking.'>
                    <InfoCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                  </Tooltip>
                </>
              ) : (
                upperCase(col?.title)
              )}
            </span>
          ),
        };

        if (col.key === 'action' && id === 'schedule' && scheduleSubtab === 'current') {
          columnConfig['render'] = (text: string) => <span>{text}</span>;
        } else if (col.key === 'action' || col.key === 'vendorName' || col.key === 'fixtureName') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            if (id === 'hr' && !record.isEditable) {
              return text;
            }

            const onClickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedRow(rowIndex);
              setSelectedLocation(record.key);
            };

            return <EditActionCell text={text} status={record.status} onClickHandler={onClickHandler} />;
          };

          if (col.key === 'vendorName') {
            columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
              const onClickHandler = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
                e.preventDefault();
                e.stopPropagation();

                if (id === 'brandedFixtures') {
                  setBrandedFixtureDrawer(true);
                  setSelectedBrandedFixture(record);
                  return;
                }

                setVendorDrawer(true);
                setSelectedVendor(record.id);
                setRowDetail(record);
              };

              return <EditActionCell text={text} record={record} onClickHandler={onClickHandler} />;
            };
          }

          if (col.key === 'fixtureName') {
            columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
              const onClickHandler = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
                e.preventDefault();
                e.stopPropagation();
                setBrandedFixtureDrawer(true);
                setSelectedBrandedFixture(record);
              };

              return <EditActionCell text={text} record={record} onClickHandler={onClickHandler} />;
            };
          }
        } else if ((col.key === 'region' || col.key === 'district') && id !== 'doctors' && isWriteAllAllowed) {
          columnConfig['ellipsis'] = false;
          columnConfig['render'] = (text: string, record: any) => {
            if (!text) {
              return null;
            }

            const onClickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalVisible(true);
              let id;

              if (col.key === 'region') {
                id = record.regionId;
              } else {
                id = record.districtId;
              }

              setModalData({
                mode: 'edit',
                key: col.key,
                id,
                regionId: col.key === 'district' ? record.regionId : null,
                name: text,
                onClose: () => setIsModalVisible(false),
              });
            };
            return <LinkCell linkText={text} onClickHandler={onClickHandler} />;
          };
        } else if (col.key === 'locationNumber') {
          columnConfig['ellipsis'] = false;
          columnConfig['render'] = (locations: any) => {
            if (!locations.length) {
              return null;
            }

            // TODO: handle location click
            return locations.map((item, index) => (
              <Tooltip
                title={item.locationName}
                key={item.locationNumber}
                color={'white'}
                placement={'topLeft'}
                overlayInnerStyle={{ color: item.primaryLocation ? '#003366' : '#5377b0' }}
                overlayStyle={{ fontSize: '11px' }}
              >
                <span>
                  <LinkCell
                    linkText={item.locationNumber}
                    onClickHandler={() => {}}
                    isPrimaryStyled={item.primaryLocation}
                    style={{ margin: 0, padding: 0 }}
                  />
                  {index !== locations.length - 1 && ', '}
                </span>
              </Tooltip>
            ));
          };
        } else if (col.key === 'validate') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            return (
              <>
                {record.validatedDate === null && (
                  <Button
                    type='primary'
                    size='small'
                    disabled={!isImportEditAllowed || record.validatedDate !== null}
                    onClick={() => {
                      dispatch(validateFile(record.officeImportId));
                    }}
                  >
                    Validate Records
                  </Button>
                )}
                {record.validatedDate !== null &&
                  (record.status === 'Validation Success' || record.processedDate !== null) && (
                    <Tooltip title='File Record validation successful, click to download report'>
                      <Button
                        type='primary'
                        size='small'
                        icon={<FileExcelFilled />}
                        style={{ background: '#43CC21', borderColor: '#43CC21' }}
                        disabled={record.validatedDate === null}
                        onClick={() => {
                          dispatch(
                            downloadValidateFile({
                              address: record.validatedFileUrl,
                              name: record.fileName,
                            })
                          );
                        }}
                      >
                        Success Report
                      </Button>
                    </Tooltip>
                  )}
                {record.validatedDate !== null &&
                  record.status === 'Validation Failed' &&
                  record.processedDate == null && (
                    <Tooltip title='File Record validation failed, click to download report'>
                      <Button
                        type='primary'
                        danger
                        icon={<FileExcelFilled />}
                        disabled={record.validatedDate === null}
                        size='small'
                        onClick={() => {
                          dispatch(
                            downloadValidateFile({
                              address: record.validatedFileUrl,
                              name: record.fileName,
                            })
                          );
                        }}
                      >
                        Failure Report
                      </Button>
                    </Tooltip>
                  )}
              </>
            );
          };
        } else if (col.key === 'processImport') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            let disabled = record.status !== 'Validation Success';
            if (record.processedDate == null)
              return isImportEditAllowed && record.status === 'Validation Success' ? (
                <>
                  <Popconfirm
                    title='Are you sure to process this import file?'
                    description='Do you want to process this import file?'
                    okText='Yes'
                    cancelText='No'
                    onConfirm={() => dispatch(processImport(record.officeImportId))}
                  >
                    <Button type='primary' size='small' disabled={disabled}>
                      Process Import File
                    </Button>
                  </Popconfirm>
                </>
              ) : (
                <Button type='primary' size='small' disabled={true}>
                  Process Import File
                </Button>
              );
            else
              return (
                <p>
                  Processed by {record.processedBy} on
                  <br /> {record.processedDate}
                </p>
              );
          };
        } else if (col.key === 'snapshot') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            return (
              <>
                <Button
                  type='primary'
                  size='small'
                  disabled={record.processedDate == null}
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(
                      downloadSnapshotFile({
                        address: record.snapshotFileUploadUrl,
                        name: record.fileName,
                      })
                    );
                  }}
                >
                  Snapshot
                </Button>
              </>
            );
          };
        } else if (col.key === 'download') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            return (
              <>
                <Button
                  type='primary'
                  size='small'
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(
                      downloadFile({
                        address: record.uploadFileUrl,
                        name: record.fileName,
                      })
                    );
                  }}
                >
                  Download
                </Button>
              </>
            );
          };
        } else if (col.key === 'deleteFile') {
          columnConfig['render'] = (text: string, record: any, rowIndex: any) => {
            let disabled = !isImportEditAllowed;
            return !disabled ? (
              <>
                <Popconfirm
                  title='Are you sure to delete this import file record?'
                  description='This will permanently delete the file. Are you sure?'
                  okText='Yes'
                  cancelText='No'
                  onConfirm={() => dispatch(deleteFile(record.officeImportId))}
                >
                  <Button
                    danger
                    type='primary'
                    size='small'
                    icon={<DeleteOutlined />}
                    disabled={!isImportEditAllowed || record.processedDate !== null}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </>
            ) : (
              <Button
                danger
                type='primary'
                size='small'
                icon={<DeleteOutlined />}
                disabled={!isImportEditAllowed || record.processedDate !== null}
              >
                Delete
              </Button>
            );
          };
        }

        if (!columnConfig?.render && !isEmpty(col)) {
          columnConfig['render'] = (text) => <TextCell value={text} />;
        }

        return columnConfig;
      }),
    [id, scheduleSubtab, scheduleDate]
  );

  const modalTitle = () => {
    const title = `${get(modalData, 'title', '').toUpperCase()}`;

    if (title) {
      return title;
    }

    const mode = `${get(modalData, 'mode', '').toUpperCase()}`;
    const keyName = modalData['key'] === 'vendorMaintenance' ? 'Office Vendor' : get(modalData, 'key', '');
    const modalName = `${keyName.toUpperCase()}`;

    return mode === 'ADD' ? `+ ${mode} NEW ${modalName}` : `${mode} ${modalName}`;
  };

  const modalContent = (key) => {
    switch (key) {
      case 'vendorMaintenance':
        return <VendorDetails modalData={modalData} />;
      case 'region':
      case 'district':
        return <AreaRegionDistrictDetails modalData={modalData} />;
      case 'location':
        return <LocationDetails onClose={() => setIsModalVisible(false)} isCreateMode={true} />;
      case 'bulkEditRegionDistrict':
        return <AreaRegionDistrictBulkEdit isAllSelected={isAllSelected} modalData={modalData} tab={activeTab} />;
    }
  };

  const getMenuItems = () => {
    if (id === 'vendorMaintenance') return ['vendorMaintenance'];
    if (id === 'brandedFixtures') return ['brandedFixtures'];
    return ['location', 'region', 'district'];
  };

  const getMenuLabel = (key: string) => {
    if (key === 'vendorMaintenance') return 'Office Vendor';
    if (key === 'brandedFixtures') return 'Branded Fixture';
    return key;
  };

  const menu = (
    <Menu className={menuStyles['menu']}>
      {getMenuItems()?.map((key) => (
        <Menu.Item className={menuStyles['menu-item']} key={`add-${key}`}>
          <a
            onClick={() => {
              if (key === 'brandedFixtures') {
                setSelectedBrandedFixture(null);
                setBrandedFixtureDrawer(true);
              } else {
                setModalData({ mode: 'add', key, onClose: () => setIsModalVisible(false) });
                setIsModalVisible(true);
              }
            }}
            className={menuStyles['link']}
          >
            {`Add ${key === 'brandedFixtures' ? getMenuLabel(key) : capitalize(getMenuLabel(key))}`}
          </a>
        </Menu.Item>
      ))}
    </Menu>
  );

  useEffect(() => {
    return () => {
      dispatch(toggleSelectedLocations([]));
      setStartSelection(null);
      setEndSelection(null);
    };
  }, []);

  useEffect(() => {
    setSelectedRow(null);
    setSelectedLocation(null);
  }, [activeTab, search]);

  useEffect(() => {
    if (tabsWithBulk.includes(activeTab) && !isNull(startSelection) && !isNull(endSelection)) {
      const locList = locations.map((item) => item.key);
      const start = indexOf(locList, startSelection);
      const end = indexOf(locList, endSelection);
      const selectedLocList = start === end ? [] : locList.slice(start, end + 1);

      if (!selectedLocList.length) {
        setStartSelection(null);
        setEndSelection(null);
      }

      dispatch(toggleSelectedLocations(selectedLocList));
    }
  }, [startSelection, endSelection]);

  const onRow = (record) => ({
    onDoubleClick: (event) => {
      if (id === 'audit' || id === 'doctors' || isAllSelected || !tabsWithBulk.includes(activeTab)) return;

      event.preventDefault();
      event.stopPropagation();

      dispatch(toggleSelectedLocations([record.key]));

      if (isBulkEditAllowed) {
        setModalData({
          mode: 'edit',
          key: 'bulkEditRegionDistrict',
          title: 'Update Details',
          onClose: () => setIsModalVisible(false),
        });
        setIsModalVisible(true);
      }

      setSelectedRow(null);
      setSelectedLocation(null);
    },
    onClick: (event) => {
      if (id === 'audit' || isAllSelected || !tabsWithBulk.includes(activeTab)) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();

        if (event.currentTarget !== event.target) {
          const { key } = record;
          dispatch(
            toggleSelectedLocations(
              selectedLocationIDs.includes(key)
                ? selectedLocationIDs.filter((item) => item !== key)
                : [...selectedLocationIDs, key]
            )
          );
        }
      }
      if (event.shiftKey) {
        event.stopPropagation();
        // @ts-ignore
        document.getSelection().removeAllRanges();

        if (event.currentTarget !== event.target) {
          const { key } = record;

          if (isNull(startSelection)) {
            setStartSelection(key);
          } else {
            setEndSelection(key);
          }
        }
      }
    },
  });

  return (
    <>
      <div className={styles['table-container']}>
        <div className={styles['table-wrapper']}>
          <Table
            className={styles['table-common']}
            onRow={onRow}
            rowClassName={(record, i) =>
              isAllSelected || selectedLocationIDs.includes(record.key) || i === selectedRow ? 'table-selected-row' : ''
            }
            dataSource={data}
            columns={columns}
            pagination={false}
            loading={loading}
            scroll={{ x: '100%', y: 'calc(100vh - 17.5em)' }}
          />

          {id !== 'audit' &&
            id !== 'doctors' &&
            id !== 'import' &&
            (isAllSelected || !!selectedLocationIDs.length ? (
              <Button
                disabled={!isBulkEditAllowed || !tabsWithBulk.includes(activeTab)}
                className={`${styles['add-btn']} ${
                  isBulkEditAllowed && tabsWithBulk.includes(activeTab) ? '' : styles['disabled']
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setModalData({
                    mode: 'edit',
                    key: 'bulkEditRegionDistrict',
                    title: 'Update Details',
                    onClose: () => setIsModalVisible(false),
                  });
                  setIsModalVisible(true);
                }}
              >
                <img src={'./icons/action-edit.svg'} alt='edit' />
              </Button>
            ) : (id == 'vendorMaintenance' && writeMenusPermissions['vendor'] == true) ||
              (id == 'brandedFixtures' && isWriteBrandedFixturesAllowed) ||
              writeTabsPermissions[id] === true ? (
              <Dropdown overlay={menu} trigger={['click']} placement='topLeft' disabled={!isAddAllowed}>
                <Button
                  className={`${styles['add-btn']} ${isAddAllowed ? '' : styles['disabled']}`}
                  onClick={(e) => e.preventDefault()}
                >
                  <img src={'./icons/action-add.svg'} alt='add' />
                </Button>
              </Dropdown>
            ) : null)}
        </div>

        <div className={styles['pagination-container']}>
          <div className={styles['pagination-actions-wrapper']}>
            <Pagination
              current={page + 1}
              defaultPageSize={size}
              pageSizeOptions={['15', '25', '50', '100']}
              showSizeChanger={true}
              total={total}
              showTotal={(total) =>
                id !== 'audit' && (
                  <div className={styles['total-container']}>
                    <div className={styles['total-title']}>
                      {!!selectedLocationIDs.length || isAllSelected ? (
                        <div className={styles['total-selected-rows']}>
                          <span>SELECTED</span>
                          <span
                            onClick={() => {
                              dispatch(toggleSelectedLocations([]));
                              setStartSelection(null);
                              setEndSelection(null);
                              toggleIsAllSelected(false);
                            }}
                          >
                            <img src={'./icons/action-close.svg'} alt='close' />
                          </span>
                        </div>
                      ) : id === 'doctors' ? (
                        'TOTAL EMPLOYEES'
                      ) : id === 'vendorMaintenance' ? (
                        'TOTAL OFFICE VENDORS'
                      ) : (
                        'TOTAL FILES'
                      )}
                    </div>
                    <div className={styles['total-count']}>
                      <span>{(isAllSelected ? total : selectedLocationIDs.length) || total}</span>
                    </div>
                  </div>
                )
              }
              onChange={(pageNumber, pageSize) => {
                setSearch({ size: pageSize, page: size !== pageSize ? 0 : pageNumber - 1 });
              }}
            />
            {id === 'audit' || id === 'doctors' || !tabsWithBulk.includes(activeTab) ? null : (
              <SelectAllButton
                whatSelect='offices'
                isAllSelected={isAllSelected}
                onClick={() => {
                  toggleIsAllSelected(!isAllSelected);
                  dispatch(toggleSelectedLocations([]));
                }}
              />
            )}
          </div>

          <img alt='logo' className={styles['logo']} src={'./images/logo.svg'} />
        </div>
      </div>

      <Drawer
        placement='right'
        visible={!!selectedLocation}
        width={id === 'doctors' || id === 'vendor' || id === 'merchandising' ? 478 : 325}
        closable={false}
        className='drawer-panel'
        forceRender={true}
        onClose={() => {
          setSelectedRow(null);
          setSelectedLocation(null);
        }}
      >
        {id === 'doctors' ? (
          <DoctorPanel
            selectedDoctorId={selectedLocation}
            hideDetails={() => {
              setSelectedRow(null);
              setSelectedLocation(null);
            }}
          />
        ) : (
          <CollapsiblePanel
            id={activeTab}
            locId={selectedLocation}
            scheduleSubtab={scheduleSubtab}
            scheduleDate={scheduleDate}
            hideDetails={() => {
              setSelectedRow(null);
              setSelectedLocation(null);
            }}
          />
        )}
      </Drawer>

      <Modal
        title={modalTitle()}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
        centered
        footer={null}
      >
        {modalContent(get(modalData, 'key'))}
      </Modal>
      <EditVendorDetails
        id={id}
        vendorId={selectedVendor}
        record={rowDetail}
        setRowDetail={setRowDetail}
        setSelectedVendor={setSelectedVendor}
        setVendorDrawer={setVendorDrawer}
        vendorDrawer={vendorDrawer}
        hideDetails={() => {
          setSelectedVendor(null);
          setVendorDrawer(false);
          setRowDetail({});
        }}
      />
      <EditBrandedFixtureDetails
        visible={brandedFixtureDrawer}
        onClose={() => {
          setBrandedFixtureDrawer(false);
          setSelectedBrandedFixture(null);
        }}
        fixtureData={selectedBrandedFixture}
      />
    </>
  );
}

export default BaseGrid;
